
import CryptoJS from 'crypto-js';

// Encryption utilities for sensitive client-side data
export class ClientEncryption {
  // Generate a session-based encryption key that's unique per session
  private static getEncryptionKey(): string {
    let sessionKey = sessionStorage.getItem('encryption_session_key');
    if (!sessionKey) {
      // Generate a new session key
      const randomBytes = new Uint8Array(32);
      crypto.getRandomValues(randomBytes);
      sessionKey = Array.from(randomBytes, byte => byte.toString(16).padStart(2, '0')).join('');
      sessionStorage.setItem('encryption_session_key', sessionKey);
    }
    return sessionKey;
  }
  
  // Generate a salt for each encryption operation
  private static generateSalt(): string {
    const salt = new Uint8Array(16);
    crypto.getRandomValues(salt);
    return Array.from(salt, byte => byte.toString(16).padStart(2, '0')).join('');
  }
  
  // Encrypt data for localStorage
  static encrypt(data: any): string {
    try {
      const jsonString = JSON.stringify(data);
      const salt = this.generateSalt();
      const key = this.getEncryptionKey() + salt; // Combine session key with unique salt
      const encrypted = CryptoJS.AES.encrypt(jsonString, key).toString();
      
      // Prepend salt to encrypted data
      return salt + ':' + encrypted;
    } catch (error) {
      console.error('Encryption failed:', error);
      throw new Error('Failed to encrypt data');
    }
  }
  
  // Decrypt data from localStorage
  static decrypt(encryptedData: string): any {
    try {
      const [salt, encrypted] = encryptedData.split(':');
      if (!salt || !encrypted) {
        console.error('Invalid encrypted data format');
        return null;
      }
      
      const key = this.getEncryptionKey() + salt;
      const bytes = CryptoJS.AES.decrypt(encrypted, key);
      const decryptedString = bytes.toString(CryptoJS.enc.Utf8);
      
      if (!decryptedString) {
        console.error('Decryption produced empty result');
        return null;
      }
      
      return JSON.parse(decryptedString);
    } catch (error) {
      console.error('Decryption failed:', error);
      return null;
    }
  }
  
  // Secure localStorage operations with data integrity check
  static setSecureItem(key: string, value: any): void {
    try {
      const encrypted = this.encrypt(value);
      const timestamp = Date.now().toString();
      const dataWithTimestamp = { data: encrypted, timestamp };
      localStorage.setItem(key, JSON.stringify(dataWithTimestamp));
    } catch (error) {
      console.error('Failed to store encrypted data:', error);
    }
  }
  
  static getSecureItem(key: string): any {
    try {
      const stored = localStorage.getItem(key);
      if (!stored) return null;
      
      const { data: encrypted, timestamp } = JSON.parse(stored);
      
      // Check if data is too old (older than 24 hours)
      const age = Date.now() - parseInt(timestamp);
      if (age > 24 * 60 * 60 * 1000) {
        this.removeSecureItem(key);
        return null;
      }
      
      return this.decrypt(encrypted);
    } catch (error) {
      console.error('Failed to retrieve encrypted data:', error);
      this.removeSecureItem(key); // Clean up corrupted data
      return null;
    }
  }
  
  static removeSecureItem(key: string): void {
    localStorage.removeItem(key);
  }
  
  // Clear all encrypted data and session key
  static clearAll(): void {
    sessionStorage.removeItem('encryption_session_key');
    // Optionally clear all localStorage items that might be encrypted
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('encrypted') || key.includes('secure') || key.includes('saved'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  }
}
